import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { useNotificationStore } from '../../stores/notificationStore';
import { applicationService } from '../../services/applicationService';
import { JobApplication } from '../../types';
import { ApplicationDetail } from './ApplicationDetail';
import { ArrowLeft, Edit } from 'lucide-react';

export const ViewApplicationPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { addNotification } = useNotificationStore();
  
  const [application, setApplication] = useState<JobApplication | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Carica la candidatura specifica da Firestore
  useEffect(() => {
    const loadApplication = async () => {
      if (!id || !user?.uid) {
        setError('ID candidatura o utente non valido');
        setLoading(false);
        return;
      }

      try {
        const app = await applicationService.getApplication(user.uid, id);
        
        if (app) {
          setApplication(app);
        } else {
          setError('Candidatura non trovata');
          addNotification({
            type: 'warning',
            title: 'Candidatura non trovata',
            message: 'La candidatura con questo ID non esiste.'
          });
        }
      } catch (error) {
        console.error('❌ Errore nel caricamento candidatura:', error);
        setError('Errore nel caricamento della candidatura');
        addNotification({
          type: 'warning',
          title: 'Errore caricamento',
          message: 'Impossibile caricare i dati della candidatura.'
        });
      } finally {
        setLoading(false);
      }
    };

    loadApplication();
  }, [id, user?.uid, addNotification]);

  // Se sta caricando, mostra loading
  if (loading) {
    return (
      <div className="w-full h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Caricamento candidatura...</p>
        </div>
      </div>
    );
  }

  // Se c'è un errore E non stiamo caricando, mostra errore
  if (!loading && (error || !application)) {
    return (
      <div className="w-full h-screen flex items-center justify-center p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center max-w-md">
          <h2 className="text-xl font-semibold text-red-800 dark:text-red-200 mb-2">
            Errore nel caricamento
          </h2>
          <p className="text-red-600 dark:text-red-300 mb-4">
            {error || 'Impossibile caricare la candidatura'}
          </p>
          <button
            onClick={() => navigate('/applications')}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Torna alle candidature
          </button>
        </div>
      </div>
    );
  }

  // Se abbiamo l'applicazione, mostra il dettaglio
  if (application) {
    return (
      <div className="w-full h-screen flex flex-col">
        

        {/* Contenuto principale - scrollabile */}
        <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900">
          <ApplicationDetail
            application={application}
            onClose={() => navigate('/applications')}
          />
        </div>
      </div>
    );
  }

  // Fallback: ancora caricamento
  return (
    <div className="w-full h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600 dark:text-gray-400">Caricamento candidatura...</p>
      </div>
    </div>
  );
}; 